import { getAllIpa } from "../phonemes/ipa";
import { segmentsByLanguage } from "./segmentsByLanguage";
import { segmentsForExtinct } from "./segmentsForExtinct";

/** handle the quirks of the language phoneme data to break into segments */
export function getLanguageSegments(segments: string): string[] {
  segments = segments.trim().replace(/"/g, '');  // some data has " in front of each phoneme
  return segments.split(/ |\|/); // space or vertical bar (e.g. 'a n̪|n')
}

/** get a language's phonemes without diacritics,
 * optionally adding anything from 'sets' that are part of the language phonemes
 */
export function getBasicPhonemesFromSegments(segments: string[], sets?: string[]): string[] {
  const { diacritics } = getAllIpa();

  const results: string[] = [];
  for (const segment of segments) {
    if (sets && sets.includes(segment)) { // part of 'sets', so add
      results.push(segment);
    } else {  // add each phoneme from the segment, without diacritics
      const phonemes = removeDiacritics(segment, diacritics).split('');
      results.push(...phonemes);
    }
  }

  const deduped: string[] = [];
  const previous = new Set<string>();
  results.forEach(s => {
    if (!previous.has(s)) {
      previous.add(s);
      deduped.push(s);
    }
  });
  return deduped;
}

function removeDiacritics(s: string, diacritics: string[]): string {
  let firstDiacritic = -1;
  for (let i = 0; i < s.length; i++) {
    if (diacritics.includes(s[i])) {
      firstDiacritic = i;
      break;
    }
  }
  return firstDiacritic === -1 ? s : s.substring(0, firstDiacritic);
}

/** return any symbols used for a language's phonemes that are unsupported by the system */
export function getUnsupported(language: string): Set<string> {
  const missing = new Set<string>();

  // get segment
  const s = getRawSegment(language);
  if (s === undefined) {
    return missing;
  }

  // find unique missing symbols
  const { ipa, diacritics } = getAllIpa();
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c !== ' ' && !ipa.includes(c) && !diacritics.includes(c)) {
      missing.add(c);
    }
  }
  return missing;
}

function getRawSegment(language: string): string | undefined {
  let s = segmentsByLanguage[language];
  if (s === undefined) {
    s = segmentsForExtinct[language];
  }
  if (s === undefined) {
    return undefined;
  }
  // a bunch have a leading " for some reason
  s = s.replace(/"/g, '');
  return s;
}

/** return all symbols used for any language's phonemes that are unsupported by the system */
export function getAllUnsupported(): Set<string> {
  const allMissing = new Set<string>();
  const all = getAllLanguages();
  all.forEach(language => {
    const missing = getUnsupported(language);
    /*
    if (missing.has('R')) {
      console.log('>> ', language, ' :: ', segmentsByLanguage[language]);
    }
    */
    missing.forEach(m => allMissing.add(m));
  });
  return allMissing;
}

/** get a sorted list of all languages we have segments for */
function getAllLanguages(): string[] {
  const phoible = Object.getOwnPropertyNames(segmentsByLanguage);
  const extinct = Object.getOwnPropertyNames(segmentsForExtinct);
  const all = phoible.concat(extinct);
  return all.sort((a, b) => { a = a.toLowerCase(); b = b.toLowerCase(); return a === b ? 0 : a > b ? 1 : 0 });
}
